import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Div, Dropdown, Text } from '@pinpoint/design-kit';
import CONSTANTS from 'src/store/constants';
import { CellRegulatoryTag, CellTagTuple } from 'src/components/models/cells';
import { ModelProp, RefreshProp } from 'src/store/typings';
import { noOp } from 'src/functions';

export function RolesDropdown({ defaultValue, onSelectRole, dropDownStyle, currentRole }) {
  const [isOpen, setIsOpen] = useState(false);
  const roles = CONSTANTS.ROLE_MAPPING[currentRole];

  return (
    <Dropdown
      h="2rem"
      isOpen={isOpen}
      onClick={() => setIsOpen((open) => !open)}
      menu={roles.map((role) => (
        <Div>{CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[role] || role]}</Div>
      ))}
      onSelect={(index) => onSelectRole(roles[index])}
      defaultValue={CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[defaultValue] || defaultValue] || defaultValue}
      {...dropDownStyle}
    />
  );
}

RolesDropdown.propTypes = {
  defaultValue: PropTypes.string,
  onSelectRole: PropTypes.func,
  dropDownStyle: PropTypes.shape({}),
  currentRole: PropTypes.string,
};

RolesDropdown.defaultProps = {
  defaultValue: 'Please select a role',
  onSelectRole: () => null,
  dropDownStyle: {},
  currentRole: 'VIEWER',
};

export function SiteRolesDropdown({ defaultValue, onSelectRole, dropDownStyle, currentRole }) {
  const [isOpen, setIsOpen] = useState(false);
  const roles = CONSTANTS.SITE_ROLE_MAPPING[currentRole] || [];

  if (defaultValue in CONSTANTS.SITE_ROLE_MAPPING && !roles.includes(defaultValue)) {
    return (
      <Text textSize="input" textColor="blue" p={{ x: 0 }}>
        {CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[defaultValue] || defaultValue] || defaultValue}
      </Text>
    );
  }

  return (
    <Dropdown
      h="2rem"
      isOpen={isOpen}
      onClick={() => setIsOpen((open) => !open)}
      menu={roles.map((role) => (
        <Div>{CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[role] || role]}</Div>
      ))}
      onSelect={(index) => onSelectRole(roles[index])}
      defaultValue={CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[defaultValue] || defaultValue] || defaultValue}
      {...dropDownStyle}
    />
  );
}

SiteRolesDropdown.propTypes = {
  defaultValue: PropTypes.string,
  onSelectRole: PropTypes.func,
  dropDownStyle: PropTypes.shape({}),
  currentRole: PropTypes.string,
};

SiteRolesDropdown.defaultProps = {
  defaultValue: 'Please select a role',
  onSelectRole: () => null,
  dropDownStyle: {},
  currentRole: CONSTANTS.ROLE.CLIENT_USER,
};

export function ModelsDropdown({ loading, models, model, onSelectModel, dropDownStyle, defaultText, valueIndices }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Autocomplete
      name="model"
      w="400px"
      hoverBg="gray200"
      focusBg="gray200"
      bg="white"
      isOpen={isOpen}
      loading={loading}
      value={
        model
          ? model.name
              .replace(`${CONSTANTS.TAL_MODEL.TAG_NAME[model.dataModelType]}: `, '')
              .replace(' (REMEDIATED)', '')
          : defaultText
      }
      valueIndices={valueIndices}
      onClick={() => setIsOpen(!isOpen)}
      menu={models.map(({ name, dataModelType }) =>
        name.replace(`${CONSTANTS.TAL_MODEL.TAG_NAME[dataModelType]}: `, '')
      )}
      menuItemPrefix={(index) =>
        models[index]
          ? CellTagTuple({
              value: {
                typeDetail: models[index]?.typeDetail,
                predictionType: models[index]?.predictionType,
                dataModelType: models[index]?.dataModelType,
                isRemediated: models[index]?.modelData?.metadataPayload?.fairness ?? false,
                dataTest: `${models[index]?.dataModelType} ${models[index]?.name}`,
                framework: models[index]?.framework,
              },
              rightMargin: '.25rem',
              bg: 'white',
            })
          : null
      }
      onSelection={(index) => onSelectModel(models[index])}
      {...dropDownStyle}
    />
  );
}

ModelsDropdown.propTypes = {
  loading: PropTypes.bool.isRequired,
  models: PropTypes.arrayOf(ModelProp).isRequired,
  model: ModelProp,
  onSelectModel: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  multiSelect: PropTypes.bool,
  valueIndices: PropTypes.arrayOf(PropTypes.number),
  defaultText: PropTypes.string,
};

ModelsDropdown.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  multiSelect: false,
  defaultText: 'Please wait...',
  valueIndices: [],
  model: null,
};

export function FairnessClassDropdown({ onSelection, classIndex }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Autocomplete
      isOpen={isOpen}
      onSelection={onSelection}
      onClick={() => setIsOpen(!isOpen)}
      value={CONSTANTS.TAL_MODEL.FAIRNESS_LABELS[classIndex]}
      menu={CONSTANTS.TAL_MODEL.FAIRNESS_LABELS}
      direction="topleft"
      data-test="fairness-class-dropdown-btn"
      DropdownButtonOptions={{
        h: '1.875rem',
        w: '2rem',
        rounded: { tr: 'md', br: 'md' },
        border: { y: '1px solid', r: '1px solid', l: '0' },
        borderColor: 'gray500',
        hoverBg: 'white',
        bg: 'white',
        align: 'center',
        justify: 'center',
        d: 'inline-block',
        p: '0',
        transition: 'all 0.2s ease-in-out',
        'data-test': 'fairness-class-dropdown-btn',
      }}
      inputStyle={{
        h: 'calc(1.875rem-1px)',
        textWeight: '500',
        bg: 'white',
        hoverBg: 'white',
        style: {
          transition: 'all 0.2s ease-in-out',
          font: 'inherit',
          color: '#333333',
        },
        'data-test': 'fairness-class-dropdown-input',
      }}
      DivOptions={{
        rounded: isOpen ? { tl: '0', bl: 'md', r: '0' } : { l: 'md', r: '0' },
        border: isOpen ? { x: '1px solid', b: '1px solid', t: '0' } : '1px solid',
        style: {
          transition: 'all 0.2s ease-in-out',
          color: '#333333',
        },
        w: '2rem',
        textColor: 'shade33',
      }}
      menuItemOptions={{
        p: { y: '.25rem', x: '.75rem' },
        textWeight: '500',
        bg: 'white',
        hoverBg: 'shadeF2',
        textColor: 'shade33',
        'data-test': 'fairness-class-dropdown-menu-item',
      }}
      menuOptions={{
        border: { t: '1px solid', x: '1px solid', b: '0' },
        borderColor: 'gray500',
        rounded: { b: '0', tl: 'md', tr: 'md' },
        pos: 'absolute',
        shadow: 0,
        minW: 'calc(100% - 2rem)',
        maxW: 'calc(100% - 2rem)',
        style: {
          transition: 'all 0.2s ease-in-out',
        },
        maxH: '250px',
        overflow: 'auto',
        m: { b: '-2px' },
      }}
    />
  );
}

FairnessClassDropdown.propTypes = {
  onSelection: PropTypes.func,
  classIndex: PropTypes.number,
};

FairnessClassDropdown.defaultProps = {
  onSelection: noOp,
  classIndex: 0,
};

export function RefreshesDropdown({
  refreshes,
  refresh,
  onSelectRefresh,
  dropDownStyle,
  multiSelect,
  defaultText,
  valueIndices,
  ...rest
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Autocomplete
      name="refreshes-dropdown"
      data-test="refreshes-dropdown"
      w="400px"
      hoverBg="gray200"
      focusBg="gray200"
      bg="white"
      isOpen={isOpen}
      value={!refresh || !refresh?.length ? defaultText : ''}
      valueIndices={valueIndices}
      multiSelect={multiSelect}
      onClick={() => setIsOpen(!isOpen)}
      h="1.875rem"
      DropdownButtonOptions={{
        h: '1.875rem',
        'data-test': 'refreshes-dropdown-btn',
      }}
      inputStyle={{
        h: 'calc(1.875rem-1px)',
        'data-test': 'refreshes-dropdown-input',
      }}
      menuItemOptions={{
        'data-test': 'refreshes-dropdown-menu-item',
      }}
      menuOptions={{
        pos: 'absolute',
        shadow: 4,
        minW: '100%',
        rounded: 'md',
        maxH: '250px',
        bg: 'white',
        overflow: 'auto',
      }}
      menu={refreshes.map(({ name }) => name)}
      onSelection={(index) => {
        multiSelect ? onSelectRefresh(index.map((i) => refreshes[i])) : onSelectRefresh(refreshes[index]);
      }}
      {...dropDownStyle}
      {...rest}
    />
  );
}

RefreshesDropdown.propTypes = {
  refreshes: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  refresh: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ),
  onSelectRefresh: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  multiSelect: PropTypes.bool,
  valueIndices: PropTypes.arrayOf(PropTypes.number),
  defaultText: PropTypes.string,
};

RefreshesDropdown.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  multiSelect: true,
  defaultText: 'Please wait...',
  valueIndices: [],
  refresh: null,
};

export function SingleRefreshDropdown({ defaultValue, onSelectRefresh, dropDownStyle, refreshes }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Dropdown
      h="2rem"
      isOpen={isOpen}
      onClick={() => setIsOpen((open) => !open)}
      menu={
        refreshes?.length ? (
          refreshes.map((refresh) => <Div>{refresh?.displayName ?? refresh?.name}</Div>)
        ) : (
          <Div>Loading...</Div>
        )
      }
      onSelect={(index) => onSelectRefresh(refreshes[index].id)}
      defaultValue={refreshes?.length ? defaultValue?.displayName ?? defaultValue?.name : 'Loading...'}
      {...dropDownStyle}
    />
  );
}

SingleRefreshDropdown.propTypes = {
  defaultValue: RefreshProp.isRequired,
  onSelectRefresh: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  refreshes: PropTypes.arrayOf(RefreshProp).isRequired,
};

SingleRefreshDropdown.defaultProps = {
  dropDownStyle: {},
};

export function FrameworksDropdown({ frameworks, framework, onSelectFramework, dropDownStyle, defaultText, ...rest }) {
  const selectedFramework = useMemo(() => frameworks.find(({ id }) => id === framework), [framework, frameworks]);

  const menu = useMemo(
    () =>
      frameworks.length
        ? ['None / Not Applicable', ...frameworks.map((f) => `${f.name} ${f.regulationTags.join(', ')}`)]
        : ['No frameworks available for this model'],
    [frameworks]
  );

  return (
    <Autocomplete
      data-test="frameworks-dropdown"
      value={selectedFramework?.regulationTags.join(', ') ?? defaultText}
      borderColor="shade66"
      w="100%"
      h="1.875rem"
      m={{ y: '.375rem' }}
      DropdownButtonOptions={{
        h: '1.875rem',
        w: '1.875rem',
        rounded: { tr: 'md', br: 'md' },
        border: { y: '1px solid', r: '1px solid', l: '0' },
        borderColor: 'shade79',
        align: 'center',
        justify: 'center',
        d: 'flex',
        p: '0',
        'data-test': 'frameworks-dropdown-btn',
      }}
      inputStyle={{
        h: 'calc(1.875rem-1px)',
        flexGrow: '1',
        p: { l: '0' },
        rounded: 'none',
        'data-test': 'frameworks-dropdown-input',
      }}
      menuItemOptions={{
        'data-test': 'frameworks-dropdown-menu-item',
      }}
      DivOptions={{
        rounded: { l: 'md', r: '0' },
        border: '1px solid',
        borderColor: 'shade79',
        p: { l: '0.75rem' },
      }}
      menu={menu}
      menuItemPrefix={(index) =>
        index // skip first item for None / Not Applicable
          ? CellRegulatoryTag({ value: { framework: frameworks[index - 1] }, textSize: 'small', m: { r: '.75rem' } })
          : null
      }
      menuItemFormat={(text, index) => (index ? text.replace(`${frameworks?.[index - 1]?.name} `, '') : text)}
      onSelection={(index) => onSelectFramework(frameworks?.[index - 1]?.id ?? null)}
      {...dropDownStyle}
      {...rest}
    />
  );
}

FrameworksDropdown.propTypes = {
  frameworks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      regulationTags: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  framework: PropTypes.number,
  onSelectFramework: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  defaultText: PropTypes.string,
};

FrameworksDropdown.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  defaultText: '',
  framework: null,
};
